// @ts-check
/**
 * @typedef {{[key: string]: unknown, id: unknown, title: unknown, kind?: string,
 *   href?: unknown, authorized?: boolean, relationships?: unknown[]}} DashboardOverviewItem
 * @typedef {{empty: string, link: string, fields: [string, string][]}} DashboardOverviewConfiguration
 * @typedef {{state?: string, request_id?: unknown, confirmed?: boolean,
 *   items?: DashboardOverviewItem[]}} DashboardOverviewDetail
 * @typedef {Document | HTMLElement} DashboardOverviewScope
 * @typedef {Window & typeof globalThis & {
 *   GCityPartnersOverviewPresentation?: {mount: (scope: DashboardOverviewScope) => void}
 * }} DashboardOverviewWindow
 */
((/** @type {DashboardOverviewWindow} */ window) => {
  'use strict';

  const OWNER = 'GCityPartnersOverviewPresentation';

  if (window[OWNER]) return;

  const ROOT = '[data-gcp-overview-module]';
  /** @type {WeakSet<HTMLElement>} */
  const mounted = new WeakSet();

  const states = new Set([
    'loading',
    'not_connected',
    'unauthorized',
    'fetch_failed',
    'empty',
    'ready',
  ]);

  const language = String(document.documentElement.lang || '').toLowerCase();
  const isZhCN = /^zh-(cn|hans|sg)(?:-|$)/.test(language);
  const isZh = /^zh(?:-|$)/.test(language);
  /** @param {string} hant @param {string} hans @param {string} en */
  const words = (hant, hans, en) => {
    if (isZhCN) return hans;
    return isZh ? hant : en;
  };

  /** @type {Record<string, string>} */
  const relationshipLabels = {
    commissioned: words('我委託', '我委托', 'Commissioned'),
    responsible: words('我負責', '我负责', 'Responsible'),
    participant: words('我參與', '我参与', 'Participant'),
  };

  /** @type {Record<string, string>} */
  const actionLabels = {
    information: words('待補資料', '待补资料', 'Information needed'),
    reply: words('待回覆', '待回复', 'Reply needed'),
    review: words('待審核', '待审核', 'Review needed'),
    confirmation: words('待確認', '待确认', 'Confirmation needed'),
  };

  /** @type {Record<string, string>} */
  const activityLabels = {
    update: words('項目更新', '项目更新', 'Project update'),
    handover: words('交接', '交接', 'Handover'),
    deliverable: words('新交付成果', '新交付成果', 'New deliverable'),
  };

  /** @type {Record<string, string>} */
  const workspaceLabels = {
    map: 'Map',
    gtender: 'GTender',
    provider: 'Provider',
  };

  /** @type {Record<string, DashboardOverviewConfiguration>} */
  const configurations = {
    projects: {
      empty: words(
        '暫無可顯示的項目（僅限本次查詢範圍）。',
        '暂无可显示的项目（仅限本次查询范围）。',
        'No projects to display in this query scope.'
      ),
      link: words('查看項目', '查看项目', 'View project'),
      fields: [
        ['organization', words('所屬機構', '所属机构', 'Organization')],
        ['status', words('項目狀態', '项目状态', 'Project status')],
        ['updated_at', words('最新更新', '最新更新', 'Last updated')],
      ],
    },

    actions: {
      empty: words(
        '本次查詢範圍沒有待辦；這不代表項目已完成。',
        '本次查询范围没有待办；这不代表项目已完成。',
        'No pending actions in this query scope; projects may still be in progress.'
      ),
      link: words('查看事項', '查看事项', 'View action'),
      fields: [
        ['project_title', words('關聯項目', '关联项目', 'Related project')],
        ['organization', words('所屬機構', '所属机构', 'Organization')],
        ['responsible_party', words('負責方', '负责方', 'Responsible party')],
        ['due_at', words('期限', '期限', 'Due date')],
      ],
    },

    activity: {
      empty: words(
        '本次查詢範圍沒有可顯示的進展。',
        '本次查询范围没有可显示的进展。',
        'No activity to display in this query scope.'
      ),
      link: words('查看進展', '查看进展', 'View activity'),
      fields: [
        ['project_title', words('關聯項目', '关联项目', 'Related project')],
        ['actor', words('操作人', '操作人', 'Actor')],
        ['organization', words('機構', '机构', 'Organization')],
        ['occurred_at', words('時間', '时间', 'Time')],
      ],
    },

    workspaces: {
      empty: words(
        '本次查詢範圍沒有可顯示的工作區入口。',
        '本次查询范围没有可显示的工作区入口。',
        'No workspace entries in this query scope.'
      ),
      link: words('進入工作區', '进入工作区', 'Open workspace'),
      fields: [['organization', words('所屬機構', '所属机构', 'Organization')]],
    },
  };

  /** @param {unknown} value */
  const clean = (value) => (typeof value === 'string' ? value.trim() : '');

  /** @template {keyof HTMLElementTagNameMap} T @param {T} tag @param {string} [className] @param {string} [text] @returns {HTMLElementTagNameMap[T]} */
  const element = (tag, className, text) => {
    const node = document.createElement(tag);

    if (className) node.className = className;
    if (text) node.textContent = text;

    return node;
  };

  /*
   * Navigation validation only, not authorization.
   *
   * Same-origin links are supported for project/action/activity details.
   * Workspace links additionally support the existing product hostnames.
   * No pathname is fabricated here.
   */
  /** @param {unknown} value @param {boolean} [workspace] */
  const safeHref = (value, workspace) => {
    const raw = clean(value);

    if (!raw) return '';

    try {
      const url = new URL(raw, window.location.origin);

      if (url.protocol !== 'https:' || url.username || url.password) {
        return '';
      }

      if (url.origin === window.location.origin) {
        return url.href;
      }

      const workspaceHosts = new Set([
        'glhksolution.com',
        'map.glhksolution.com',
        'gtender.glhksolution.com',
      ]);

      if (workspace && !url.port && workspaceHosts.has(url.hostname)) {
        return url.href;
      }

      return '';
    } catch {
      // Invalid URLs are rejected as navigation targets; this is not an authorization fallback.
      return '';
    }
  };

  /** @param {unknown} value @param {string} module @returns {value is DashboardOverviewItem} */
  const validItem = (value, module) => {
    const item = /** @type {DashboardOverviewItem | null | undefined} */ (value);
    if (
      !item ||
      typeof item !== 'object' ||
      Array.isArray(item) ||
      !clean(item.id) ||
      !clean(item.title)
    ) {
      return false;
    }

    if (module === 'workspaces') {
      if (typeof item.kind !== 'string' || !Object.hasOwn(workspaceLabels, item.kind)) {
        return false;
      }

      /*
       * An explicit backend authorization projection is required.
       * Never derive this from payment, account labels or login alone.
       */
      if (item.kind === 'provider' && item.authorized !== true) {
        return false;
      }

      if (!safeHref(item.href, true)) {
        return false;
      }
    }

    return true;
  };

  /** @param {HTMLElement} card @param {DashboardOverviewItem} item @param {string} module */
  const addBadges = (card, item, module) => {
    /** @type {string[]} */
    const labels = [];

    if (module === 'projects') {
      const relationships = Array.isArray(item.relationships) ? item.relationships : [];

      relationships.forEach((key) => {
        if (typeof key === 'string' && Object.hasOwn(relationshipLabels, key)) {
          labels.push(relationshipLabels[key]);
        }
      });

      if (!labels.length) {
        labels.push(words('項目關係未提供', '未提供项目关系', 'Project relationship unavailable'));
      }
    }

    if (
      module === 'actions' &&
      typeof item.kind === 'string' &&
      Object.hasOwn(actionLabels, item.kind)
    ) {
      labels.push(actionLabels[item.kind]);
    }

    if (
      module === 'activity' &&
      typeof item.kind === 'string' &&
      Object.hasOwn(activityLabels, item.kind)
    ) {
      labels.push(activityLabels[item.kind]);
    }

    if (
      module === 'workspaces' &&
      typeof item.kind === 'string' &&
      Object.hasOwn(workspaceLabels, item.kind)
    ) {
      labels.push(workspaceLabels[item.kind]);
    }

    if (!labels.length) return;

    const badges = element('div', 'gcp-overview-module__badges');

    [...new Set(labels)].forEach((label) => {
      badges.appendChild(element('span', 'gcp-overview-module__badge', label));
    });

    card.appendChild(badges);
  };

  /** @param {DashboardOverviewItem} item @param {string} module @param {DashboardOverviewConfiguration} configuration */
  const renderCard = (item, module, configuration) => {
    const card = element('article', 'gcp-overview-module__card');

    card.appendChild(element('h3', '', clean(item.title)));

    addBadges(card, item, module);

    if (clean(item.summary)) {
      card.appendChild(element('p', 'gcp-overview-module__summary', clean(item.summary)));
    }

    const metadata = element('dl', 'gcp-overview-module__meta');

    configuration.fields.forEach(([key, label]) => {
      const row = element('div');
      const value = clean(item[key]);

      row.appendChild(element('dt', '', label));
      row.appendChild(element('dd', '', value || words('未提供', '未提供', 'Not provided')));

      metadata.appendChild(row);
    });

    card.appendChild(metadata);

    const href = safeHref(item.href, module === 'workspaces');

    if (href) {
      const link = element('a', 'gcp-overview-module__link', configuration.link);

      link.href = href;
      link.dataset.noLegacyNormalize = 'true';

      card.appendChild(link);
    }

    return card;
  };

  /** @param {HTMLElement} root */
  const mount = (root) => {
    if (mounted.has(root)) return;

    const module = root.dataset.gcpOverviewModule;
    if (!module) return;
    const configuration = configurations[module];

    if (!configuration) return;

    const status = root.querySelector('[data-module-status]');
    const list = /** @type {HTMLElement | null} */ (root.querySelector('[data-module-list]'));
    const retry = /** @type {HTMLButtonElement | null} */ (
      root.querySelector('[data-module-retry]')
    );

    if (!status || !list || !retry) return;

    mounted.add(root);

    /** @type {Record<string, string>} */
    const messages = {
      loading: words(
        '正在確認存取權限及讀取資料…',
        '正在确认访问权限并读取数据…',
        'Checking access and loading data…'
      ),
      not_connected: words(
        '暫時無法載入資料，請稍後重試。',
        '暂时无法加载数据，请稍后重试。',
        'Unable to load data. Please retry.'
      ),
      unauthorized: words(
        '無法存取此資料，請確認登入及所需權限。',
        '无法访问此数据，请确认登录及所需权限。',
        'Access denied. Check your sign-in and permissions.'
      ),
      fetch_failed: words(
        '資料讀取異常，請重試。這不代表沒有記錄。',
        '数据读取失败，请重试。这不代表没有记录。',
        'Data could not be loaded. Retry; this does not mean there are no records.'
      ),
      empty: configuration.empty,
      ready: words('已載入本次返回的資料。', '已加载本次返回的数据。', 'Data loaded.'),
    };

    let activeRequestId = '';
    let requestSequence = 0;
    /** @type {number | null} */
    let timer = null;

    const clearTimer = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };

    /** @param {string} state */
    const retryLabel = (state) =>
      state === 'unauthorized'
        ? words('重新確認存取權限', '重新确认访问权限', 'Check access again')
        : words('重新嘗試', '重新尝试', 'Retry');

    /** @param {DashboardOverviewDetail | null | undefined} detail */
    const apply = (detail) => {
      if (!detail || typeof detail.state !== 'string' || !states.has(detail.state)) return;

      /*
       * A host response to a retry must echo the request_id.
       * Unsolicited refreshes are accepted only when no retry is pending.
       */
      if (activeRequestId && clean(detail.request_id) !== activeRequestId) {
        return;
      }

      if (!activeRequestId && clean(detail.request_id)) {
        return;
      }

      let state = detail.state;
      const items = detail.items;
      // An obsolete async response must not restore private cards after
      // sign-out or session loss. Only the backend grants access to real data.
      const guestGate = root.closest('[data-gcitypartners-dashboard][data-gcp-guest-gate]');
      if (
        state === 'ready' &&
        guestGate &&
        (!(guestGate instanceof HTMLElement) ||
          guestGate.dataset.gcpNativeSessionState !== 'authenticated')
      ) {
        state = 'unauthorized';
      }

      if (
        state === 'empty' &&
        !(detail.confirmed === true && Array.isArray(items) && items.length === 0)
      ) {
        state = 'fetch_failed';
      }

      if (
        state === 'ready' &&
        !(
          detail.confirmed === true &&
          Array.isArray(items) &&
          items.length > 0 &&
          items.every((item) => validItem(item, module))
        )
      ) {
        state = 'fetch_failed';
      }

      if (state !== 'loading') {
        clearTimer();
        activeRequestId = '';
      }

      root.dataset.runtimeState = state;
      // D07=B: a denied module is not presented as an available workspace.
      // The host remains the authorization owner; do not infer access locally.
      root.hidden = state === 'unauthorized';
      root.setAttribute('aria-busy', String(state === 'loading'));

      status.textContent = messages[state];

      list.replaceChildren();
      list.hidden = state !== 'ready';

      retry.disabled = state === 'loading';
      retry.hidden = state === 'ready' || state === 'empty';

      retry.textContent = retryLabel(state);

      if (state !== 'ready' || !Array.isArray(items)) return;

      const fragment = document.createDocumentFragment();

      items.forEach((item) => {
        fragment.appendChild(renderCard(item, module, configuration));
      });

      list.appendChild(fragment);
    };

    root.addEventListener(
      'gcp:overview-state',
      (/** @type {Event & {detail?: DashboardOverviewDetail}} */ event) => {
        if (event.target !== root) return;
        apply(event.detail);
      }
    );

    retry.addEventListener('click', () => {
      clearTimer();

      requestSequence += 1;
      activeRequestId = [module, Date.now(), requestSequence].join('-');

      const requestId = activeRequestId;

      apply({
        state: 'loading',
        request_id: requestId,
      });

      const request = new CustomEvent('gcp:overview-request', {
        bubbles: true,
        cancelable: true,
        detail: {
          module,
          request_id: requestId,
        },
      });

      root.dispatchEvent(request);

      /*
       * Host calls preventDefault() only when it owns this request.
       * No host means not_connected, not empty.
       */
      if (!request.defaultPrevented) {
        apply({
          state: 'not_connected',
          request_id: requestId,
        });

        return;
      }

      /*
       * A synchronous host response may already have completed.
       */
      if (activeRequestId !== requestId) return;

      timer = window.setTimeout(() => {
        apply({
          state: 'fetch_failed',
          request_id: requestId,
        });
      }, 30000);
    });

    /*
     * The session/data owner must send this immediately when the visible
     * identity or organization scope changes. This clears old private data
     * and invalidates responses from the previous scope.
     */
    root.addEventListener(
      'gcp:overview-reset',
      (/** @type {Event & {detail?: DashboardOverviewDetail}} */ event) => {
        if (event.target !== root) return;

        clearTimer();
        activeRequestId = '';

        apply({
          state: event.detail?.state === 'unauthorized' ? 'unauthorized' : 'not_connected',
        });
      }
    );

    root.addEventListener('gcp:overview-dispose', () => {
      clearTimer();
      activeRequestId = '';
    });

    if (module === 'projects') {
      window.queueMicrotask(() => {
        if (root.isConnected && root.dataset.runtimeState === 'not_connected') {
          retry.click();
        }
      });
    }
  };

  /** @param {DashboardOverviewScope} scope */
  const mountWithin = (scope) => {
    if ('matches' in scope && scope.matches?.(ROOT)) mount(scope);
    scope.querySelectorAll(ROOT).forEach((root) => mount(/** @type {HTMLElement} */ (root)));
  };

  /** @param {DashboardOverviewScope} scope */
  const disposeWithin = (scope) => {
    /** @param {Element} root */
    const dispose = (root) => {
      root.dispatchEvent(new CustomEvent('gcp:overview-dispose'));
    };

    if ('matches' in scope && scope.matches?.(ROOT)) dispose(scope);
    scope.querySelectorAll(ROOT).forEach(dispose);
  };

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target && 'querySelectorAll' in event.target) {
      mountWithin(/** @type {HTMLElement} */ (event.target));
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target && 'querySelectorAll' in event.target) {
      disposeWithin(/** @type {HTMLElement} */ (event.target));
    }
  });

  window[OWNER] = Object.freeze({
    mount: mountWithin,
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountWithin(document), { once: true });
  } else {
    mountWithin(document);
  }
})(/** @type {DashboardOverviewWindow} */ (window));
